// src/pages/BookkeepingPage.js — Bank registers, pending transactions, batch deposits
// [marker: bk-register-frontend v1]
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ChartOfAccounts from '../components/ChartOfAccounts'; // [marker: coa v1]
import PaymentsDeposits from '../components/PaymentsDeposits'; // [marker: payments-deposits v1]
import AccountRegister from '../components/AccountRegister'; // [marker: registers v1]
import Vendors from '../components/Vendors'; // [marker: vendors v1]
import Bills from '../components/Bills'; // [marker: bills v1]
import CreditMemos from '../components/CreditMemos'; // [marker: books-pro v1]
import Classes from '../components/ClassSelect';
import { ClosingDateButton } from '../components/ClassReports';
import { useAuth } from '../context/AuthContext';

const fmt = (n) => `$${parseFloat(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';
const toDateInput = (d) => {
  if (!d) return new Date().toISOString().split('T')[0];
  const s = String(d);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0,10) : new Date(d).toISOString().split('T')[0];
};

export default function BookkeepingPage() {
  const { isManagerOrAbove } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    try { return sessionStorage.getItem('bk:tab') || 'payments'; } catch { return 'payments'; }
  });
  useEffect(() => { try { sessionStorage.setItem('bk:tab', activeTab); } catch {} }, [activeTab]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [registerData, setRegisterData] = useState(null);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [unclearedLines, setUnclearedLines] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allowEditGenerated, setAllowEditGenerated] = useState(false);
  const [showAddModal, setShowAddModal] = useState(null); // 'transaction' | 'batch' | 'vendor'
  const [editEntry, setEditEntry] = useState(null);        // register row when editing

  const loadRegister = useCallback(() => {
    if (!selectedAccount) return;
    setLoading(true);
    api.get(`/bookkeeping/register/${selectedAccount.id}`)
      .then(r => setRegisterData(r.data))
      .catch(() => toast.error('Failed to load register.'))
      .finally(() => setLoading(false));
  }, [selectedAccount]);

  const loadPending = useCallback(() => {
    api.get('/bookkeeping/batch-deposits/pending').then(r => setPendingDeposits(r.data)).catch(console.error);
    api.get('/bookkeeping/register/uncleared').then(r => setUnclearedLines(r.data)).catch(console.error);
  }, []);

  // [marker: coa v1] bank + credit card accounts; reloaded after Chart of Accounts edits (renames)
  const loadAccounts = useCallback(() => {
    api.get('/bookkeeping/accounts?flat=true&type=bank,credit_card')
      .then(r => {
        const list = r.data || [];
        setAccounts(list);
        setSelectedAccount(cur => (cur && list.find(a => a.id === cur.id)) || list[0] || null);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    loadAccounts();
    api.get('/bookkeeping/vendors').then(r => setVendors(r.data)).catch(console.error);
    api.get('/settings/org').then(r => setAllowEditGenerated(r.data?.bookkeeping_allow_edit_generated === 'true')).catch(console.error);
  }, []);

  useEffect(() => {
    // registers load themselves (AccountRegister)
    if (activeTab === 'pending') loadPending();
  }, [activeTab, selectedAccount, loadRegister, loadPending]);

  const openAdd = () => { setEditEntry(null); setShowAddModal('transaction'); };
  const openEdit = (row) => { setEditEntry(row); setShowAddModal('transaction'); };
  const closeModal = () => { setShowAddModal(null); setEditEntry(null); };

  const handleClear = async (lineId) => {
    try {
      await api.patch(`/bookkeeping/register/line/${lineId}/clear`, { isCleared: true, clearedDate: new Date().toISOString().split('T')[0] });
      toast.success('Marked as cleared.');
      loadPending();
      if (activeTab === 'register') loadRegister();
    } catch { toast.error('Failed to update.'); }
  };

  const canEdit = (row) => row.entry_type === 'manual' || allowEditGenerated;
  const pendingCount = unclearedLines.length;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Bookkeeping</h1>
        <div style={{display:'flex',gap:8}}>
          <ClosingDateButton />
        </div>
      </div>

      <div className="tabs">
        {['payments','register','bills','credits','pending','accounts','classes','vendors'].map(tab => (
          <button key={tab} className={`tab-btn${activeTab===tab?' active':''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'payments' ? 'Payments & Deposits' : tab === 'register' ? 'Registers' : tab === 'bills' ? 'Bills & Checks' : tab === 'credits' ? 'Customer Credits' : tab === 'classes' ? 'Classes' : tab === 'pending' ? 'Not Yet Cleared' : tab === 'accounts' ? 'Chart of Accounts' : 'Vendors'}
          </button>
        ))}
      </div>

      {/* Payments & Deposits — [marker: payments-deposits v1] */}
      {activeTab === 'payments' && (
        <PaymentsDeposits canEdit={isManagerOrAbove()} onChanged={() => {}} />
      )}

      {/* Account registers — [marker: registers v1] */}
      {activeTab === 'register' && (
        <AccountRegister canEdit={isManagerOrAbove()} initialAccountId={selectedAccount?.id || null} />
      )}

      {activeTab === 'bills' && <Bills canEdit={isManagerOrAbove()} />}
      {activeTab === 'credits' && <CreditMemos canEdit={isManagerOrAbove()} />}
      {activeTab === 'classes' && <Classes canEdit={isManagerOrAbove()} />}

      {/* Pending Transactions */}
      {activeTab === 'pending' && (
        <div>
          {pendingCount === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">✅</div><h3>Nothing pending</h3><p>Every bank transaction has cleared.</p></div>
          ) : (
            <>
              {/* Uncleared register transactions */}
              <div className="card" style={{marginBottom:20}}>
                <div className="card-header"><span className="card-title">Not yet cleared at the bank</span></div>
                <div className="card-body" style={{padding:0}}>
                  {unclearedLines.length === 0 ? (
                    <div style={{padding:20,fontSize:'0.875rem',color:'var(--gray-500)'}}>Every recorded transaction has cleared.</div>
                  ) : (
                    <div className="table-wrap" style={{border:'none'}}>
                      <table>
                        <thead><tr><th>Date</th><th>Account</th><th>Description</th><th>Payee</th><th>Check #</th><th style={{textAlign:'right'}}>Debit</th><th style={{textAlign:'right'}}>Credit</th><th></th></tr></thead>
                        <tbody>
                          {unclearedLines.map(l => (
                            <tr key={l.line_id}>
                              <td style={{fontSize:'0.8rem'}}>{fmtDate(l.entry_date)}</td>
                              <td style={{fontSize:'0.8rem',color:'var(--gray-500)'}}>{l.account_name}</td>
                              <td style={{fontSize:'0.875rem'}}>{l.line_description||l.entry_description}</td>
                              <td style={{fontSize:'0.8rem',color:'var(--gray-500)'}}>{l.payee||'—'}</td>
                              <td style={{fontSize:'0.8rem',fontFamily:'monospace'}}>{l.check_number||'—'}</td>
                              <td style={{textAlign:'right',color:'var(--success)',fontSize:'0.875rem'}}>{parseFloat(l.debit)>0?fmt(l.debit):''}</td>
                              <td style={{textAlign:'right',color:'var(--danger)',fontSize:'0.875rem'}}>{parseFloat(l.credit)>0?fmt(l.credit):''}</td>
                              <td style={{textAlign:'right'}}><button className="btn btn-secondary btn-sm" onClick={() => handleClear(l.line_id)}>Mark cleared</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <p className="form-hint">Checks and card payments waiting to go to the bank are under <strong>Payments &amp; Deposits</strong>.</p>
            </>
          )}
        </div>
      )}

      {/* Chart of Accounts — [marker: coa v1] */}
      {activeTab === 'accounts' && (
        <ChartOfAccounts
          canEdit={isManagerOrAbove()}
          onChanged={loadAccounts}
          onOpenRegister={a => { setSelectedAccount(a); setActiveTab('register'); }}
        />
      )}

      {/* Vendors — [marker: vendors v1] */}
      {activeTab === 'vendors' && <Vendors canEdit={isManagerOrAbove()} />}

      {showAddModal === 'transaction' && <TransactionModal account={selectedAccount} allAccounts={accounts} editEntry={editEntry} onClose={closeModal} onSaved={() => { closeModal(); setActiveTab('register'); loadRegister(); }} />}
      {showAddModal === 'batch' && <BatchDepositModal pending={pendingDeposits} accounts={accounts.filter(a => a.account_type === 'bank')} onClose={() => setShowAddModal(null)} onSaved={() => { setShowAddModal(null); loadPending(); if (activeTab==='register') loadRegister(); }} />}
      {showAddModal === 'vendor' && <AddVendorModal onClose={() => setShowAddModal(null)} onSaved={() => { setShowAddModal(null); api.get('/bookkeeping/vendors').then(r => setVendors(r.data)); }} />}
    </div>
  );
}

function TransactionModal({ account, allAccounts, editEntry, onClose, onSaved }) {
  const isEdit = !!editEntry;
  const initialDirection = isEdit ? (parseFloat(editEntry.debit) > 0 ? 'in' : 'out') : 'in';
  const [form, setForm] = useState({
    entryDate: isEdit ? toDateInput(editEntry.entry_date) : new Date().toISOString().split('T')[0],
    direction: initialDirection,
    amount: isEdit ? String(parseFloat(editEntry.debit) > 0 ? editEntry.debit : editEntry.credit) : '',
    payee: isEdit ? (editEntry.payee || '') : '',
    checkNumber: isEdit ? (editEntry.check_number || '') : '',
    description: isEdit ? (editEntry.line_description || editEntry.entry_description || '') : '',
    categoryAccountId: '',
    paymentMethod: 'check',
    transferToAccountId: '',
  });
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [incomeAccounts, setIncomeAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(() => {
    if (isEdit) return; // categories/method not used when editing an existing entry
    api.get('/bookkeeping/accounts?flat=true&type=expense').then(r => setExpenseAccounts(r.data)).catch(console.error);
    api.get('/bookkeeping/accounts?flat=true&type=income').then(r => setIncomeAccounts(r.data)).catch(console.error);
  }, [isEdit]);

  const handleSave = async () => {
    if (!form.amount || !form.entryDate) return toast.error('Date and amount required.');
    if (!isEdit && form.direction === 'transfer' && !form.transferToAccountId) return toast.error('Choose a destination account for the transfer.');
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/bookkeeping/register/entry/${editEntry.entry_id}`, {
          entryDate: form.entryDate,
          description: form.description,
          payee: form.payee,
          checkNumber: form.checkNumber,
          amount: form.amount,
          direction: form.direction === 'in' ? 'debit' : 'credit',
        });
        toast.success('Transaction updated.');
      } else if (form.direction === 'in') {
        await api.post(`/bookkeeping/register/${account.id}/transaction`, {
          entryDate: form.entryDate, transactionType: 'deposit', amount: form.amount,
          payee: form.payee, checkNumber: form.checkNumber, description: form.description,
          offsetAccountId: form.categoryAccountId || undefined,
        });
        toast.success('Deposit recorded.');
      } else if (form.direction === 'out') {
        if (form.categoryAccountId) {
          // Expense: records the expense (Event P&L / vendor) AND posts a bank line so it shows in the register
          await api.post('/bookkeeping/expenses', {
            expenseDate: form.entryDate, vendorName: form.payee, accountId: form.categoryAccountId,
            paymentAccountId: account.id, amount: form.amount, paymentMethod: form.paymentMethod,
            checkNumber: form.checkNumber, description: form.description,
          });
          toast.success('Expense recorded.');
        } else {
          await api.post(`/bookkeeping/register/${account.id}/transaction`, {
            entryDate: form.entryDate, transactionType: 'payment', amount: form.amount,
            payee: form.payee, checkNumber: form.checkNumber, description: form.description,
          });
          toast.success('Payment recorded.');
        }
      } else { // transfer
        await api.post(`/bookkeeping/register/${account.id}/transaction`, {
          entryDate: form.entryDate, transactionType: 'transfer', amount: form.amount,
          description: form.description, transferToAccountId: parseInt(form.transferToAccountId),
        });
        toast.success('Transfer recorded.');
      }
      onSaved();
    } catch(err) { toast.error(err.response?.data?.error||'Failed.'); }
    finally { setSaving(false); }
  };

  const otherAccounts = (allAccounts||[]).filter(a => a.id !== account?.id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title">{isEdit ? 'Edit Transaction' : 'Add Transaction'} — {account?.name}</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Type *</label>
            <select className="form-select" value={form.direction} onChange={e=>set('direction',e.target.value)}>
              <option value="in">Money in — Deposit (Debit)</option>
              <option value="out">Money out — Payment (Credit)</option>
              {!isEdit && <option value="transfer">Transfer to another account</option>}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Date *</label><input className="form-input" type="date" value={form.entryDate} onChange={e=>set('entryDate',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Amount *</label><input className="form-input" type="number" step="0.01" value={form.amount} onChange={e=>set('amount',e.target.value)}/></div>
          </div>

          {form.direction === 'transfer' ? (
            <div className="form-group"><label className="form-label">Transfer to *</label>
              <select className="form-select" value={form.transferToAccountId} onChange={e=>set('transferToAccountId',e.target.value)}>
                <option value="">Select account...</option>
                {otherAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          ) : (
            <>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Payee</label><input className="form-input" value={form.payee} onChange={e=>set('payee',e.target.value)} placeholder="Who it's to/from..."/></div>
                <div className="form-group"><label className="form-label">Check #</label><input className="form-input" value={form.checkNumber} onChange={e=>set('checkNumber',e.target.value)}/></div>
              </div>

              {!isEdit && form.direction === 'out' && (
                <>
                  <div className="form-group"><label className="form-label">Expense Category</label>
                    <select className="form-select" value={form.categoryAccountId} onChange={e=>set('categoryAccountId',e.target.value)}>
                      <option value="">None (plain payment)</option>
                      {expenseAccounts.map(a => <option key={a.id} value={a.id}>{a.parent_name?`${a.parent_name} — `:''}{a.name}</option>)}
                    </select>
                    <p className="form-hint">Pick a category to record this as an expense (feeds P&amp;L and Event reports).</p>
                  </div>
                  {form.categoryAccountId && (
                    <div className="form-group"><label className="form-label">Payment Method</label>
                      <select className="form-select" value={form.paymentMethod} onChange={e=>set('paymentMethod',e.target.value)}>
                        <option value="check">Check</option><option value="ach">ACH/Wire</option>
                        <option value="credit_card">Credit Card</option><option value="cash">Cash</option><option value="online">Online</option>
                      </select>
                    </div>
                  )}
                </>
              )}

              {!isEdit && form.direction === 'in' && incomeAccounts.length > 0 && (
                <div className="form-group"><label className="form-label">Income Category</label>
                  <select className="form-select" value={form.categoryAccountId} onChange={e=>set('categoryAccountId',e.target.value)}>
                    <option value="">None</option>
                    {incomeAccounts.map(a => <option key={a.id} value={a.id}>{a.parent_name?`${a.parent_name} — `:''}{a.name}</option>)}
                  </select>
                </div>
              )}

              <div className="form-group"><label className="form-label">Description</label><input className="form-input" value={form.description} onChange={e=>set('description',e.target.value)}/></div>
            </>
          )}

          {isEdit && editEntry.entry_type !== 'manual' && (
            <p className="form-hint" style={{color:'var(--danger)'}}>This is an auto-generated transaction. Editing it here will not change the original source record beyond the linked amounts.</p>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving...':(isEdit?'Save Changes':'Save')}</button></div>
      </div>
    </div>
  );
}

function BatchDepositModal({ pending, accounts, onClose, onSaved }) {
  const [selected, setSelected] = useState(pending.map(p=>p.id));
  const [form, setForm] = useState({ depositDate: new Date().toISOString().split('T')[0], bankAccountId: accounts[0]?.id||'', notes: '' });
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setSelected(s => s.includes(id)?s.filter(i=>i!==id):[...s,id]);
  const total = pending.filter(p=>selected.includes(p.id)).reduce((s,p)=>s+parseFloat(p.amount),0);
  const handlePost = async () => {
    if (!selected.length||!form.depositDate||!form.bankAccountId) return toast.error('Select payments, date, and account.');
    setSaving(true);
    try {
      const res = await api.post('/bookkeeping/batch-deposits/post', { paymentIds:selected, depositDate:form.depositDate, bankAccountId:parseInt(form.bankAccountId), notes:form.notes });
      toast.success(res.data.message);
      onSaved();
    } catch(err) { toast.error(err.response?.data?.error||'Failed.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title">Post Batch Deposit</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-row" style={{marginBottom:16}}>
            <div className="form-group"><label className="form-label">Deposit Date *</label><input className="form-input" type="date" value={form.depositDate} onChange={e=>setForm(f=>({...f,depositDate:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Bank Account *</label>
              <select className="form-select" value={form.bankAccountId} onChange={e=>setForm(f=>({...f,bankAccountId:e.target.value}))}>
                {accounts.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{background:'var(--gray-50)',borderRadius:'var(--radius)',padding:12,marginBottom:12,fontSize:'0.875rem',fontWeight:600}}>
            Selected: {selected.length} payments — Total: {fmt(total)}
          </div>
          <div style={{maxHeight:300,overflowY:'auto'}}>
            {pending.map(p=>(
              <div key={p.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid var(--gray-100)'}}>
                <input type="checkbox" checked={selected.includes(p.id)} onChange={()=>toggle(p.id)} style={{cursor:'pointer'}}/>
                <div style={{flex:1,fontSize:'0.875rem'}}>
                  <strong>{p.business_name}</strong> — {p.invoice_number}
                  {p.check_number&&<span style={{color:'var(--gray-500)'}}> · Check #{p.check_number}</span>}
                  {p.payer_name&&<span style={{color:'var(--gray-500)'}}> · {p.payer_name}</span>}
                </div>
                <div style={{fontWeight:700,color:'var(--success)'}}>{fmt(p.amount)}</div>
              </div>
            ))}
          </div>
          <div className="form-group" style={{marginTop:12}}><label className="form-label">Notes</label><input className="form-input" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Optional deposit notes..."/></div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handlePost} disabled={saving||!selected.length}>{saving?'Posting...':'Post Deposit'}</button></div>
      </div>
    </div>
  );
}

function AddVendorModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name:'', contactName:'', phone:'', email:'', paymentTerms:'net_30', notes:'' });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const handleSave = async () => {
    if (!form.name) return toast.error('Vendor name required.');
    setSaving(true);
    try {
      await api.post('/bookkeeping/vendors', form);
      toast.success('Vendor added.');
      onSaved();
    } catch(err) { toast.error(err.response?.data?.error||'Failed.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title">Add Vendor</span><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Vendor Name *</label><input className="form-input" value={form.name} onChange={e=>set('name',e.target.value)} autoFocus/></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Contact Name</label><input className="form-input" value={form.contactName} onChange={e=>set('contactName',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e=>set('phone',e.target.value)}/></div>
          </div>
          <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)}/></div>
          <div className="form-group"><label className="form-label">Payment Terms</label>
            <select className="form-select" value={form.paymentTerms} onChange={e=>set('paymentTerms',e.target.value)}>
              <option value="due_on_receipt">Due on Receipt</option><option value="net_15">Net 15</option><option value="net_30">Net 30</option><option value="net_60">Net 60</option>
            </select>
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving...':'Add Vendor'}</button></div>
      </div>
    </div>
  );
}
